import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  uploadToStorage,
  addModeleRecord,
  updateModeleRecord,
  loadAppSettingsHybrid,
  ensureAppSettings,
  reconcileDossierFieldsForModele,
  rebuildDossierFields,
} from "../services/data";
import TemplateValidator from "../components/TemplateValidator";
import { useNotifications } from "../components/NotificationProvider.jsx";

const ICONS = [
  "file-contract",
  "file-invoice",
  "file-signature",
  "file-alt",
  "file-pdf",
  "handshake",
  "signature",
  "stamp",
];

export default function UploadModele({
  user,
  onBack,
  onTryUse,
  editingModele,
  onOpenManualConfig,
}) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [folder, setFolder] = useState("");
  const [icon, setIcon] = useState("file-contract");
  const [templateFile, setTemplateFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [validation, setValidation] = useState({
    ok: false,
    errors: [],
    warnings: [],
    info: [],
  });
  const [dossierTypes, setDossierTypes] = useState([]);
  const [validatorKey, setValidatorKey] = useState("");
  const [templatePreviewURL, setTemplatePreviewURL] = useState(null);
  const initialOldRef = useRef(null);
  const isExistingModele = Boolean(editingModele && editingModele.id);
  const { notify } = useNotifications();
  const bumpValidatorKey = (tpl, cfg = null) => {
    const f1 = tpl ? `${tpl.name}-${tpl.size}-${tpl.lastModified}` : "no-tpl";
    const f2 = cfg ? `${cfg.name}-${cfg.size}-${cfg.lastModified}` : "no-cfg";
    setValidatorKey(`${f1}__${f2}__${Date.now()}`);
  };
  // Initialiser les champs si on est en mode édition
  useEffect(() => {
    if (editingModele) {
      const treatAsEditing = Boolean(
        editingModele.id || editingModele.__fromMarketplace
      );
      setIsEditing(treatAsEditing);
      setName(editingModele.nom || "");
      setDesc(editingModele.description || "");
      setFolder(editingModele.dossier || "");
      setIcon(editingModele.icon || "file-contract");
      setTemplateFile(null);
      setValidation({ ok: true, errors: [], warnings: [], info: [] });
      initialOldRef.current = {
        dossier: editingModele.dossier || "",
        fields_meta: editingModele.fields_meta || {},
      };
      setTemplatePreviewURL(editingModele.fileURL || null);
    } else {
      setIsEditing(false);
      setName("");
      setDesc("");
      setFolder("");
      setIcon("file-contract");
      setTemplateFile(null);
      setValidation({ ok: false, errors: [], warnings: [], info: [] });
      initialOldRef.current = null;
      setTemplatePreviewURL(null);
    }
  }, [editingModele, isExistingModele]);
  useEffect(() => {
    const canUseObjectURL =
      typeof window !== "undefined" &&
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL === "function" &&
      typeof URL.revokeObjectURL === "function";

    if (!templateFile || !canUseObjectURL) {
      setTemplatePreviewURL(editingModele?.fileURL || null);
      return undefined;
    }
    const objectURL = URL.createObjectURL(templateFile);
    setTemplatePreviewURL(objectURL);
    return () => {
      URL.revokeObjectURL(objectURL);
    };
  }, [templateFile, editingModele?.fileURL]);
  const ensuredOnceRef = useRef(false);
  useEffect(() => {
    if (!user?.uid) return;
    // 1) cache immédiat (localStorage) puis 2) live Firestore (1 read initial)
    const unsub = loadAppSettingsHybrid(user.uid, async (app) => {
      // Amorçage: si le doc n'existe pas encore, crée-le UNE fois
      if (!app && !ensuredOnceRef.current) {
        ensuredOnceRef.current = true;
        try {
          const created = await ensureAppSettings(user.uid);
          app = created;
        } catch (e) {
          console.warn("ensureAppSettings failed:", e?.message || e);
        }
      }
      const data = app?.dossiers || {};
      const types = (data?.types || []).map((t) => ({
        key: t.key,
        label: t.label || t.name || t.key,
        icon: t.icon || "fas fa-folder",
      }));
      setDossierTypes(types);
    });
    return () => {
      try {
        unsub && unsub();
      } catch {}
    };
  }, [user?.uid]);

  const sizeFmt = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return "Taille inconnue";
    const k = 1024;
    const units = ["Bytes", "KB", "MB", "GB", "TB"];
    const idx = Math.min(
      units.length - 1,
      Math.floor(Math.log(n) / Math.log(k))
    );
    return `${(n / Math.pow(k, idx)).toFixed(2)} ${units[idx]}`;
  };

  const hasRemoteConfig = Boolean(
    editingModele?.configURL || editingModele?.configPath
  );
  const hasInlineConfig = Boolean(
    !hasRemoteConfig && editingModele?.configInline
  );
  const inlineConfigUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (inlineConfigUrlRef.current) {
        URL.revokeObjectURL(inlineConfigUrlRef.current);
        inlineConfigUrlRef.current = null;
      }
    };
  }, [editingModele?.configInline]);

  const openInlineConfig = () => {
    if (!editingModele?.configInline) return;
    if (inlineConfigUrlRef.current) {
      URL.revokeObjectURL(inlineConfigUrlRef.current);
      inlineConfigUrlRef.current = null;
    }
    const blob = new Blob(
      [JSON.stringify(editingModele.configInline, null, 2)],
      {
        type: "application/json",
      }
    );
    inlineConfigUrlRef.current = URL.createObjectURL(blob);
    window.open(inlineConfigUrlRef.current, "_blank");
  };

  const canSubmit = useMemo(
    () =>
      Boolean(
        name.trim() &&
          (templateFile || isEditing) &&
          (isEditing || validation.ok)
      ),
    [name, templateFile, isEditing, validation.ok]
  );

  const onCancelAndTry = () => {
    if (!templateFile && !isEditing) {
      notify({
        type: "warning",
        title: "Test local impossible",
        message:
          "Sélectionne au moins un fichier .docx pour tester sans upload.",
      });
      return;
    }
    if (!isExistingModele && !validation.ok) {
      notify({
        type: "error",
        title: "Validation requise",
        message:
          "Le modèle contient des erreurs. Corrige-les avant le test (voir panneau).",
      });
      return;
    }

    let tplURL = "";
    if (templateFile) {
      tplURL = URL.createObjectURL(templateFile);
    } else if (editingModele?.__localTemplateFile) {
      tplURL = URL.createObjectURL(editingModele.__localTemplateFile);
    } else if (editingModele?.fileURL) {
      tplURL = editingModele.fileURL;
    }

    let cfgURL = editingModele?.configURL || "";
    if (!cfgURL && editingModele?.configInline) {
      const blob = new Blob([JSON.stringify(editingModele.configInline)], {
        type: "application/json",
      });
      cfgURL = URL.createObjectURL(blob);
    }

    const modeleTemp = {
      id: isEditing ? editingModele?.id : "temp-local",
      nom:
        name.trim() ||
        templateFile?.name ||
        editingModele?.nom ||
        "Modèle local",
      description: desc.trim(),
      dossier: folder || "Propositions",
      type: isEditing ? editingModele?.type || "Contrat" : "Contrat",
      source: isEditing ? editingModele?.source || "Personnalisé" : "Local",
      icon,
      modifie: new Date().toLocaleDateString(),
      utilise: (isEditing ? editingModele?.utilise : 0) ?? 0,
      fileURL: tplURL,
      storagePath: isEditing ? editingModele?.storagePath || "" : "",
      configURL: cfgURL,
      configPath: isEditing ? editingModele?.configPath || "" : "",
      configInline: editingModele?.configInline || null,
      __localTemplateFile:
        templateFile || editingModele?.__localTemplateFile || null,
      size: templateFile?.size || editingModele?.size || 0,
      ext: templateFile
        ? (templateFile.name.split(".").pop() || "docx").toLowerCase()
        : editingModele?.ext || "docx",
    };

    onTryUse && onTryUse(modeleTemp);
  };

  const onSubmit = async () => {
    if (!user?.uid) return;
    if (!canSubmit) {
      notify({
        type: "warning",
        title: "Formulaire incomplet",
        message: "Nom + fichier .docx requis.",
      });
      return;
    }
    if (!isExistingModele && !validation.ok) {
      notify({
        type: "error",
        title: "Validation requise",
        message:
          "Corrige d'abord les erreurs du modèle (panneau de validation).",
      });
      return;
    }

    try {
      setSuccess("");
      setProgress(1);

      // 1) déterminer quel fichier on doit vraiment uploader
      let fileToUpload = templateFile;

      // cas spécial : je reviens d'une config en ligne sur un modèle déjà créé,
      // mais le modèle n'a PAS encore de fileURL alors que j'avais donné un docx local
      if (
        !fileToUpload &&
        isEditing &&
        !editingModele?.fileURL &&
        editingModele?.__localTemplateFile
      ) {
        fileToUpload = editingModele.__localTemplateFile;
      }

      // 2) uploader si on a un fichier à envoyer
      let upTemplate = null;
      if (fileToUpload) {
        upTemplate = await uploadToStorage({
          uid: user.uid,
          file: fileToUpload,
          folder: "modeles",
          contentType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          onProgress: setProgress,
        });
      }

      const prevDossierKey = isExistingModele
        ? initialOldRef.current?.dossier || ""
        : null;
      const prevFieldsMeta = isExistingModele
        ? initialOldRef.current?.fields_meta || {}
        : {};
      const resolvedFieldsMeta =
        editingModele?.fields_meta || prevFieldsMeta;
      const docxMetaFromValidator =
        validation?.docx ||
        editingModele?.docxMeta ||
        editingModele?.docx_meta ||
        null;
      // 3) Mise à jour ou création du record Firestore
      const modeleData = {
        nom: name.trim(),
        description: desc.trim(),
        dossier: folder || "Propositions",
        type: editingModele?.type || "Contrat",
        source: editingModele?.source || "Personnalisé",
        icon: /^(fas|far|fal|fab)\sfa-/.test(icon) ? icon : `fas fa-${icon}`,
        modifie: new Date().toLocaleDateString(),
        updatedAt: Date.now(),
        utilise:
          (isExistingModele ? editingModele?.utilise : 0) ?? 0, // Sécuriser les champs potentiellement vides
        fileURL: upTemplate?.fileURL ?? editingModele?.fileURL ?? "",
        storagePath:
          upTemplate?.storagePath ?? editingModele?.storagePath ?? "",
        configURL: editingModele?.configURL ?? "",
        configPath: editingModele?.configPath ?? "",
        configInline: editingModele?.configInline ?? null,
        ...(docxMetaFromValidator
          ? {
              docxMeta: docxMetaFromValidator,
              docx_meta: docxMetaFromValidator,
            }
          : {}),

        size: templateFile?.size || editingModele?.size || 0,
        ext: templateFile
          ? templateFile.name.split(".").pop()?.toLowerCase() || ""
          : editingModele?.ext || "docx",
        ...(Object.keys(resolvedFieldsMeta || {}).length
          ? {
              fields_meta: resolvedFieldsMeta,
              fields_meta_version: editingModele?.fields_meta_version || 1,
            }
          : {}),
      };
      console.log("Données à enregistrer:", modeleData);

      if (isExistingModele) {
        await updateModeleRecord(user.uid, editingModele.id, modeleData);
      } else {
        await addModeleRecord(user.uid, modeleData);
      }

      // [fix] éviter de relire une vieille version au retour (cache DossierPage)
      try {
        localStorage.removeItem(`modeles_cache:${user.uid}`);
      } catch {}
      // 4) Mettre à jour le registre par dossier (agrégation pour EditDashboard)
      try {
        const nextDossierKey = modeleData.dossier || "Propositions";
        const nextFieldsMeta = resolvedFieldsMeta;

        console.log("reconcileDossierFieldsForModele", {
          prevDossierKey,
          nextDossierKey,
          oldCount: Object.keys(prevFieldsMeta || {}).length,
          newCount: Object.keys(nextFieldsMeta || {}).length,
        });

        await reconcileDossierFieldsForModele(user.uid, {
          oldDossierKey: prevDossierKey,
          newDossierKey: nextDossierKey,
          oldFields: prevFieldsMeta,
          newFields: nextFieldsMeta,
        });

        // Rebuild ciblé pour garantir la cohérence après un move/update
        await Promise.all([
          prevDossierKey
            ? rebuildDossierFields(user.uid, prevDossierKey)
            : Promise.resolve(),
          rebuildDossierFields(user.uid, nextDossierKey),
        ]);
      } catch (e) {
        console.warn("reconcile/rebuild warning:", e?.message || e);
      }
      setProgress(100);
      setSuccess(
        `Modèle " ${name} " ${
          isExistingModele ? "modifié" : "uploadé"
        } avec succès !`
      );

      setTimeout(() => onBack?.(), 1200);
    } catch (e) {
      console.error("UploadModele failed:", e);
      const code = e?.code || e?.name || "unknown_error";
      const msg = e?.message || JSON.stringify(e);
      notify({
        type: "error",
        title: isExistingModele ? "Erreur de modification" : "Erreur d'upload",
        message: `[${code}] ${msg}`,
      });
      setProgress(0);
    }
  };
  // au-dessus du return, ajoute :
  const blockReason = (() => {
    if (!name.trim()) return "Le nom du modèle est requis.";
    if (!isEditing && !templateFile) return "Sélectionne un fichier .docx.";
    if (!isEditing && templateFile && !validation.ok)
      return "Corrige d'abord les erreurs (voir la section Vérification).";
    return "";
  })();

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          {isEditing ? "Modifier le modèle" : "Uploader un modèle"}
          {templateFile && (
            <span className="badge-opt" style={{ marginLeft: 8 }}>
              {validation.errors.length} err / {validation.warnings.length} warn
            </span>
          )}
        </h1>
        <div className="page-actions">
          <button className="btn btn-outline" onClick={onBack}>
            <i className="fas fa-arrow-left"></i> Retour
          </button>
          <button className="btn btn-success" onClick={onCancelAndTry}>
            <i className="fas fa-bolt"></i> Tester{" "}
            {isEditing ? "les modifications" : "sans uploader"}
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              notify({
                type: "info",
                title: "Aide format docx",
                message:
                  "Aide: délimiteurs {champ}, sections {#section} * {/section}. Config JSON/Excel optionnelle.",
              })
            }
          >
            <i className="fas fa-question-circle"></i> Aide
          </button>
        </div>
      </div>

      {/* Afficher les fichiers actuels en mode édition */}
      {isEditing && (
        <div className="upload-card">
          <div className="upload-col">
            <h3 className="upload-title">
              <i className="fas fa-file-word"></i> Modèle actuel
            </h3>
            <div className="file-row">
              <i className="fas fa-file-word file-ico"></i>
              <div className="file-meta">
                <div className="file-name">
                  {editingModele?.nom ||
                    editingModele?.name ||
                    "Modèle sans nom"}
                </div>
                <div className="file-size">
                  {sizeFmt(editingModele?.size)}
                </div>
              </div>
            </div>
            {!editingModele?.fileURL && (
              <div className="muted" style={{ marginTop: 6 }}>
                Aucun fichier distant détecté pour ce modèle.
              </div>
            )}
          </div>

          {(hasRemoteConfig || hasInlineConfig) && (
            <div className="upload-col">
              <h3 className="upload-title">
                <i className="fas fa-file-code"></i> Configuration actuelle
              </h3>
              <div className="file-row">
                <i className="fas fa-file-code file-ico"></i>
                <div className="file-meta">
                  <div className="file-name">
                    {hasRemoteConfig
                      ? "Configuration (fichier)"
                      : "Configuration (inline)"}
                  </div>
                  <div className="file-size">Configuration enregistrée</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      <TemplateValidator
        key={validatorKey}
        templateFile={templateFile}
        onValidationChange={setValidation}
      />

      {templateFile && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ marginBottom: 6 }}>
            <strong>Vérification :</strong> {validation.errors.length}{" "}
            erreur(s), {validation.warnings.length} avertissement(s)
          </div>

          {validation.errors.length > 0 && (
            <div className="alert-error">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Erreurs bloquantes
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validation.errors.slice(0, 5).map((e, i) => (
                  <li key={`e-${i}`}>{e.msg}</li>
                ))}
              </ul>
              {validation.errors.length > 5 && (
                <div className="muted" style={{ marginTop: 6 }}>
                  *et {validation.errors.length - 5} de plus (voir le panneau de
                  vérification au-dessus).
                </div>
              )}
            </div>
          )}

          {validation.warnings.length > 0 && (
            <div className="alert-warning" style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>
                Avertissements
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validation.warnings.slice(0, 5).map((w, i) => (
                  <li key={`w-${i}`}>{w.msg}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {/* Bloc sélection fichiers */}
      <div className="upload-card">
        <div className="upload-col">
          <h3 className="upload-title">
            <i className="fas fa-file-word"></i>
            {isEditing ? "Nouveau modèle Word (.docx)" : "Modèle Word (.docx)"}
            <span className={isEditing ? "badge-opt" : "badge-req"}>
              {isEditing ? "Optionnel" : "Requis"}
            </span>
          </h3>
          <p className="muted">
            {isEditing
              ? "Remplace le modèle actuel"
              : "Fichier Word contenant vos placeholders"}
          </p>
          <label className="btn btn-outline" style={{ cursor: "pointer" }}>
            <i className="fas fa-upload"></i> Choisir un fichier
            <input
              type="file"
              accept=".docx"
              style={{ display: "none" }}
              onClick={(e) => {
                e.target.value = "";
              }} // Force onChange même si le même fichier est sélectionné
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setTemplateFile(f);
                bumpValidatorKey(f); // Re-monte le TemplateValidator
              }}
            />
          </label>
          {templateFile && (
            <div className="file-row">
              <i className="fas fa-file-word file-ico"></i>
              <div className="file-meta">
                <div className="file-name">{templateFile.name}</div>
                <div className="file-size">{sizeFmt(templateFile.size)}</div>
              </div>
              <button
                className="icon-btn danger"
                onClick={() => setTemplateFile(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          )}
        </div>

        <div className="upload-col">
          <h3 className="upload-title">
            <i className="fas fa-file-code"></i> Configurer le modèle
            <span className="badge-opt">Optionnel</span>
          </h3>
          <p className="muted">
            Ouvre l'éditeur en ligne pour définir ou modifier les placeholders.
            Les imports Excel/JSON se font directement dans l'éditeur.
          </p>
          <div className="btn-row">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                const docxMetaFromValidation = validation?.docx || null;
                const existingDocxMeta =
                  editingModele?.docxMeta || editingModele?.docx_meta || null;
                const docxMeta =
                  docxMetaFromValidation || existingDocxMeta || null;

                const stubModele = editingModele?.id
                  ? {
                      ...editingModele,
                      docxMeta,
                      docx_meta: docxMeta,
                      __localTemplateFile:
                        editingModele?.__localTemplateFile ||
                        templateFile ||
                        null,
                    }
                  : {
                      id: null,
                      nom:
                        name?.trim() ||
                        templateFile?.name ||
                        editingModele?.nom ||
                        "Modele local",
                      description: desc || editingModele?.description || "",
                      dossier: folder || editingModele?.dossier || "",
                      icon: icon || editingModele?.icon || "fas fa-file",
                      fileURL: editingModele?.fileURL || undefined,
                      storagePath: editingModele?.storagePath || undefined,
                      docxMeta,
                      docx_meta: docxMeta,
                      fields_meta: editingModele?.fields_meta || {},
                      configInline: editingModele?.configInline || null,
                      configURL: editingModele?.configURL || "",
                      configPath: editingModele?.configPath || "",
                      __localTemplateFile:
                        templateFile ||
                        editingModele?.__localTemplateFile ||
                        null,
                    };

                if (!editingModele?.id && !templateFile && !docxMeta) {
                  notify({
                    type: "info",
                    title: "Astuce",
                    message:
                      "Astuce : charge un .docx pour extraire automatiquement les cles. Tu peux quand meme ouvrir l'editeur et tout saisir a la main.",
                  });
                }

                onOpenManualConfig?.(stubModele);
              }}
              title="Ouvrir l'éditeur de configuration manuelle"
            >
              <i className="fas fa-sliders-h"></i> Configurer en ligne
            </button>
          </div>
        </div>
      </div>

      {/* Config formulaire */}
      <div className="config-card">
        <h3 className="config-title">Configuration du modèle</h3>
        <div className="config-grid">
          <div className="form-group">
            <label className="form-label">Nom du modèle *</label>
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Contrat de services"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Dossier d'enregistrement</label>
            <select
              className="form-select"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
            >
              <option value="">-- Sélectionner un dossier --</option>
              {dossierTypes.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Décrivez l'usage de ce modèle..."
            />
          </div>
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label className="form-label">Icône</label>
            <div className="icon-grid">
              {ICONS.map((k) => (
                <button
                  type="button"
                  key={k}
                  className={`icon-cell ${icon === k ? "active" : ""}`}
                  onClick={() => setIcon(k)}
                >
                  <i className={`fas fa-${k}`}></i>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Progress + actions */}
      {progress > 0 && (
        <div className="upload-progress">
          <div className="upload-bar" style={{ width: `${progress}%` }} />
          <span>{progress}%</span>
        </div>
      )}
      {success && (
        <div className="alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      <div className="actions-end">
        <button className="btn btn-outline" onClick={onBack}>
          Annuler
        </button>
        <button
          className="btn btn-primary"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          <i className="fas fa-upload"></i>{" "}
          {isEditing ? "Modifier" : "Uploader"} le modèle
        </button>
        {blockReason && (
          <div className="alert-warning" style={{ marginBottom: 8 }}>
            <i className="fas fa-exclamation-circle"></i> {blockReason}
          </div>
        )}
      </div>
    </div>
  );
}
